import React, { useState, useEffect, useRef } from "react";
import { debuggerService } from "../services/DebuggerService";
import {
  DebuggerSession,
  ReconstructSessionResponse,
  CompareMessage,
  BenchmarkResults,
} from "../types/debuggerServiceTypes";
import SessionList from "../components/debugger/SessionList";
import SessionReconstruction from "../components/debugger/SessionReconstruction";
import CompareMessagesTable from "../components/debugger/CompareMessagesTable";
import BenchmarkControls from "../components/debugger/benchmarkControls";
import LoadingSpinner from "../components/LoadingSpinner";
import Header from "../components/Header";
import { formatDateTime } from "../utils/textUtils";
import FlowchartIframe, {
  FlowchartIframeHandle,
} from "../components/debugger/FlowchartIframe";

interface DebuggerPageProps {
  updateAvailable?: boolean;
  onUpdateAction?: () => void;
}

const DebuggerPage: React.FC<DebuggerPageProps> = ({
  updateAvailable = false,
  onUpdateAction,
}) => {
  const [sessions, setSessions] = useState<DebuggerSession[]>([]);
  const [selectedSession, setSelectedSession] =
    useState<DebuggerSession | null>(null);
  const [reconstructedSession, setReconstructedSession] =
    useState<ReconstructSessionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"sessions" | "messages">(
    "sessions"
  );
  const [loadMedia, setLoadMedia] = useState(false);

  // Compare messages state
  const [compareMessages, setCompareMessages] = useState<CompareMessage[]>([]);
  const [compareMessagesLoading, setCompareMessagesLoading] = useState(false);
  const [hideReviewed, setHideReviewed] = useState(false);
  const [showReviewedOnly, setShowReviewedOnly] = useState(false);
  const [benchmarkResults, setBenchmarkResults] =
    useState<BenchmarkResults | null>(null);
  const [numReviewedMessages, setNumReviewedMessages] = useState(0);

  const iframeRef = useRef<FlowchartIframeHandle>(null);

  useEffect(() => {
    if (activeTab === "sessions") {
      loadSessions();
    } else if (activeTab === "messages") {
      loadCompareMessages();
    }
  }, [activeTab]);

  const loadSessions = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await debuggerService.getSessions();
      setSessions(response.sessions);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load sessions");
    } finally {
      setLoading(false);
    }
  };

  const loadCompareMessages = async () => {
    setCompareMessagesLoading(true);
    setError(null);

    try {
      // Load all messages without filtering - filtering will be done in the frontend
      const response = await debuggerService.getCompareMessages();
      setCompareMessages(response.messages);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load compare messages"
      );
    } finally {
      setCompareMessagesLoading(false);
    }
  };

  const handleSessionSelect = async (session: DebuggerSession) => {
    setSelectedSession(session);
    setLoadMedia(false); // Reset loadMedia when selecting a new session
    setLoading(true);
    setError(null);

    try {
      const reconstructed = await debuggerService.reconstructSession({
        session_id: session.session_id,
      });
      setReconstructedSession(reconstructed);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to reconstruct session"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleMarkReviewed = async (sessionId: string, reviewed: boolean) => {
    try {
      // Find the session to get the session_create_time
      const session = sessions.find((s) => s.session_id === sessionId);
      if (!session || !session.session_create_time) {
        throw new Error("Session not found or missing creation time");
      }

      await debuggerService.logSession({
        session_id: sessionId,
        session_create_time: session.session_create_time,
        has_session_been_reviewed: reviewed ? "true" : "false",
      });

      // Update the session in the list
      setSessions((prevSessions) =>
        prevSessions.map((session) =>
          session.session_id === sessionId
            ? { ...session, has_session_been_reviewed: reviewed }
            : session
        )
      );

      // Update selected session if it's the current one
      if (selectedSession?.session_id === sessionId) {
        setSelectedSession((prev) =>
          prev ? { ...prev, has_session_been_reviewed: reviewed } : null
        );
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update review status"
      );
    }
  };

  const handleUpdateCompareMessage = async (
    queryId: string,
    updates: Partial<CompareMessage>
  ) => {
    try {
      await debuggerService.updateCompareMessage(queryId, updates);

      // Update the message in local state
      setCompareMessages((prevMessages) =>
        prevMessages.map((message) =>
          message.query_id === queryId ? { ...message, ...updates } : message
        )
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update compare message"
      );
    }
  };

  const handleToggleHideReviewed = () => {
    const newHideReviewed = !hideReviewed;
    setHideReviewed(newHideReviewed);
    // If hiding reviewed, uncheck show reviewed only
    if (newHideReviewed && showReviewedOnly) {
      setShowReviewedOnly(false);
    }
  };

  const handleToggleShowReviewedOnly = () => {
    const newShowReviewedOnly = !showReviewedOnly;
    setShowReviewedOnly(newShowReviewedOnly);
    // If showing reviewed only, uncheck hide reviewed
    if (newShowReviewedOnly && hideReviewed) {
      setHideReviewed(false);
    }
  };

  const handleBenchmarkResults = (results: BenchmarkResults) => {
    setBenchmarkResults(results);
  };

  const handleBenchmarkResultsUpdate = (results: BenchmarkResults) => {
    setBenchmarkResults(results);
  };

  const handleReviewedCountChange = (count: number) => {
    setNumReviewedMessages(count);
  };

  return (
    <div className="min-h-screen bg-[#e3f2fd] w-screen">
      <Header
        title=""
        updateAvailable={updateAvailable}
        onUpdateAction={onUpdateAction}
      />

      {/* Mobile-only message */}
      <div className="lg:hidden bg-[#f1f9fe] border-b border-blue-200 p-4 min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <svg
            className="h-16 w-16 text-[#00a8ca] mx-auto mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
          <h2 className="text-xl font-semibold text-[#00a8ca] mb-2">
            Desktop Only
          </h2>
          <p className="text-[#00a8ca]">
            This debugger page requires a desktop view for the best experience.
            Please access this page on a larger screen.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="hidden min-h-[1600px] lg:flex max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-col h-[calc(100vh-120px)]">
        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4 flex-shrink-0">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Hidden-but-mounted iframe */}
        <FlowchartIframe
          ref={iframeRef}
          iframeSrc="https://avetalanguages.com/OnlineBuilder"
          onExport={() => {}} //TODO: Does this need handleOnExport
          hideOpenButton
        />

        {/* Tab Navigation */}
        <div className="mb-3 flex-shrink-0">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab("sessions")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "sessions"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Review Sessions ({sessions.length})
            </button>
            <button
              onClick={() => setActiveTab("messages")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "messages"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Review Messages
            </button>
          </nav>
        </div>

        {activeTab === "sessions" ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 flex-1 min-h-0">
            {/* Session List */}
            <div className="lg:col-span-1 min-h-0">
              <div className="bg-white rounded-lg shadow h-full flex flex-col">
                <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
                  <h2 className="text-lg font-medium text-gray-900">
                    All Sessions
                  </h2>
                </div>

                {loading ? (
                  <div className="p-6 flex-1 flex items-center justify-center">
                    <LoadingSpinner />
                  </div>
                ) : (
                  <div className="flex-1 overflow-y-auto min-h-0">
                    <SessionList
                      sessions={sessions}
                      selectedSession={selectedSession}
                      onSessionSelect={handleSessionSelect}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Session Reconstruction */}
            <div className="lg:col-span-2 min-h-0">
              <div className="bg-white rounded-lg shadow h-full flex flex-col">
                <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-medium text-gray-900">
                        Session Reconstruction
                      </h2>
                      {selectedSession && (
                        <div className="mt-1 text-sm text-gray-500">
                          <p>Session ID: {selectedSession.session_id}</p>
                          {selectedSession.user_email && (
                            <p>User: {selectedSession.user_email}</p>
                          )}
                          {selectedSession.session_create_time && (
                            <p>
                              Started:{" "}
                              {formatDateTime(
                                selectedSession.session_create_time
                              )}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {selectedSession && (
                      <div className="flex space-x-3">
                        {/* Load Media Button */}
                        {reconstructedSession && (
                          <button
                            onClick={() => setLoadMedia(!loadMedia)}
                            className={`inline-flex px-3 py-2 text-sm leading-4 font-medium rounded-md transition-colors ${
                              loadMedia
                                ? "bg-gray-600 hover:bg-gray-700 text-white"
                                : "bg-blue-600 hover:bg-blue-700 text-white"
                            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                          >
                            <svg
                              className="h-4 w-4 mr-2"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                              />
                            </svg>
                            {loadMedia ? "Hide Media" : "Load Media"}
                          </button>
                        )}

                        {/* Mark Reviewed Button */}
                        <button
                          onClick={() =>
                            handleMarkReviewed(
                              selectedSession.session_id,
                              !selectedSession.has_session_been_reviewed
                            )
                          }
                          className="inline-flex px-3 py-2 text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                        >
                          <svg
                            className="h-4 w-4 mr-2"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          {selectedSession.has_session_been_reviewed
                            ? "Mark Unreviewed"
                            : "Mark Reviewed"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {loading ? (
                  <div className="p-6 flex-1 flex items-center justify-center">
                    <LoadingSpinner />
                  </div>
                ) : reconstructedSession ? (
                  <div className="flex-1 overflow-hidden min-h-0">
                    <SessionReconstruction
                      session={reconstructedSession}
                      loadMedia={loadMedia}
                    />
                  </div>
                ) : (
                  <div className="p-6 text-center text-gray-500 flex-1 flex flex-col items-center justify-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      No session selected
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Choose a session from the list to view the conversation.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : compareMessagesLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <BenchmarkControls
              onBenchmarkResults={handleBenchmarkResults}
              numReviewedMessages={numReviewedMessages}
            ></BenchmarkControls>

            <CompareMessagesTable
              messages={compareMessages}
              hideReviewed={hideReviewed}
              onToggleHideReviewed={handleToggleHideReviewed}
              showReviewedOnly={showReviewedOnly}
              onToggleShowReviewedOnly={handleToggleShowReviewedOnly}
              onUpdateMessage={handleUpdateCompareMessage}
              benchmarkResults={benchmarkResults}
              onReviewedCountChange={handleReviewedCountChange}
              onBenchmarkResultsUpdate={handleBenchmarkResultsUpdate}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DebuggerPage;
